import { UseQueryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiGmwMonitoringtubesListRequest, MonitoringTube } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { BroPageResponse, fetchAllPages } from "./helpers/fetch-all-pages";
import { queryDirectly } from "./helpers/query-directly";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

export type GmwMonitoringTubesRequestWithGmwBroId = ApiGmwMonitoringtubesListRequest & {
  gmwBroId: string | undefined;
};

export type GmnMonitoringTubesRequestWithGmwBroId = ApiGmwMonitoringtubesListRequest & {
  gmnBroId: string | undefined;
};

type AllowedRequest = GmwMonitoringTubesRequestWithGmwBroId | GmnMonitoringTubesRequestWithGmwBroId;

// ─── LIST ─────────────────────────────────────────────────────────────────────

// Fetches all pages for the given filter options — does not require gmw so it can be
// used with other filters such as gmwBroId or gmnBroId.
const getGmwMonitoringtubesAllPagesQuery = ({
  gmwBroId,
  gmnBroId,
  ...options
}: AllowedRequest) => ({
  queryKey: ["gmw-monitoringtubes-all", gmwBroId, gmnBroId, simplifyQueryParam(options)],
  queryFn: () =>
    fetchAllPages((...pageOptions) =>
      uploadApi.apiGmwMonitoringtubesList({ gmwBroId, gmnBroId, ...options, ...pageOptions })
    ),
  enabled: !!gmwBroId || !!gmnBroId,
});

export const useGmwMonitoringtubesAllPagesQuery = <TData = BroPageResponse<MonitoringTube>>(
  options: AllowedRequest,
  queryOptions?: Omit<
    UseQueryOptions<BroPageResponse<MonitoringTube>, Error, TData>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({ ...getGmwMonitoringtubesAllPagesQuery(options), ...queryOptions });
};

export const useGmwMonitoringtubesAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: AllowedRequest) =>
    queryDirectly(queryClient, getGmwMonitoringtubesAllPagesQuery(options));
};

// ─── INVALIDATION ────────────────────────────────────────────────────────────
